import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import multiMonthPlugin from '@fullcalendar/multimonth';
import interactionPlugin from '@fullcalendar/interaction';
import frLocale from '@fullcalendar/core/locales/fr';
import { format, addDays, startOfWeek, endOfWeek, eachDayOfInterval } from 'date-fns';
import { fr } from 'date-fns/locale';
import OnCallForm from './OnCallForm';
import { useTechnicians } from '../../contexts/TechniciansContext';
import { useSkills } from '../../contexts/SkillsContext';
import { useOnCalls } from '../../contexts/OnCallsContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useTeam } from '../../context/TeamContext';
import { useTeamPermissions } from '../../hooks/useTeamPermissions';
import { API_CONFIG, teamsService, renderEmailTemplate } from '../../services/api';

// Couleurs prédéfinies pour les compétences
const SKILL_COLORS = [
  '#FF5733', '#33FF57', '#3357FF', '#FF33F5', '#33FFF5',
  '#FFB533', '#B5FF33', '#33B5FF', '#FF33B5', '#33FFB5'
];

const PlanningCalendar = ({ onEditOnCall, refreshKey, onDataChange }) => {
  const calendarRef = useRef(null);
  const { sidebarOpen } = useSidebar();
  const { canEdit, canDelete } = useTeamPermissions();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [emailConfirmOpen, setEmailConfirmOpen] = useState(false);
  const [selectedOnCall, setSelectedOnCall] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [events, setEvents] = useState([]);
  const [skills, setSkills] = useState({});
  const [initialDate, setInitialDate] = useState(() => {
    // Récupérer la date sauvegardée depuis localStorage
    const savedDate = localStorage.getItem('calendarDate');
    return savedDate || new Date().toISOString().split('T')[0];
  });
  const [emailData, setEmailData] = useState({
    to: '',
    from: '',
    subject: '',
    message: '',
    html: ''
  });
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const { technicians, loading: techniciansLoading } = useTechnicians();
  const { skills: skillsData, loading: skillsLoading } = useSkills();
  const { onCalls, deleteOnCall, getOnCallById, loading: onCallsLoading } = useOnCalls();
  const { selectedTeam } = useTeam();

  useEffect(() => {
    const loadData = async () => {
      try {
        console.log('=== CHARGEMENT DES DONNÉES PlanningCalendar ===');
        console.log('Technicians loading:', techniciansLoading);
        console.log('Skills loading:', skillsLoading);
        console.log('OnCalls loading:', onCallsLoading);

        // Attendre que toutes les données soient chargées
        if (techniciansLoading || skillsLoading || onCallsLoading) {
          console.log('Données en cours de chargement...');
          return;
        }

        console.log('Skills data:', skillsData);
        console.log('OnCalls data:', onCalls);
        console.log('Technicians data:', technicians);

        // Créer un map des compétences
        const skillsMap = skillsData.reduce((acc, skill) => {
          acc[skill.id] = skill;
          return acc;
        }, {});
        setSkills(skillsMap);

        // Créer un map des techniciens pour un accès rapide
        const techniciansMap = technicians.reduce((acc, tech) => {
          acc[tech.id] = tech;
          return acc;
        }, {});

        console.log('Skills map:', skillsMap);
        console.log('Technicians map:', techniciansMap);
        console.log('Processing', onCalls.length, 'on-calls');

        // Traiter chaque astreinte
        const formattedEvents = onCalls.map(onCall => {
          const technician = techniciansMap[onCall.technicianId];
          if (!technician) {
            console.warn('Technicien non trouvé pour ID:', onCall.technicianId);
            return null;
          }

          // Créer le dégradé de couleurs à partir des compétences
          const skillColors = onCall.skills
            .map(skillId => skillsMap[skillId]?.color)
            .filter(Boolean);

          let backgroundStyle;
          if (skillColors.length > 0) {
            if (skillColors.length === 1) {
              backgroundStyle = skillColors[0];
            } else {
              const stripeWidth = 100 / skillColors.length;
              const gradientStops = skillColors.flatMap((color, index) => [
                `${color} ${index * stripeWidth}%`,
                `${color} ${(index + 1) * stripeWidth}%`
              ]);
              backgroundStyle = `linear-gradient(90deg, ${gradientStops.join(', ')})`;
            }
          } else {
            backgroundStyle = '#f5f5f5';
          }

          // S'assurer que les dates sont correctement formatées
          const startDateStr = typeof onCall.startDate === 'string' 
            ? onCall.startDate 
            : onCall.startDate.toISOString().split('T')[0];
          const endDateStr = typeof onCall.endDate === 'string' 
            ? onCall.endDate 
            : onCall.endDate.toISOString().split('T')[0];

          // Utiliser les dates exactes sans ajustement artificiel
          // FullCalendar s'attend à une date de fin exclusive, donc +1 jour seulement pour la fin
          const startDateForCalendar = startDateStr; // Date de début exacte
          
          // Calculer la date de fin exclusive sans conversion UTC
          const endDate = new Date(endDateStr);
          endDate.setDate(endDate.getDate() + 1); // +1 jour pour rendre la date de fin exclusive
          const year = endDate.getFullYear();
          const month = String(endDate.getMonth() + 1).padStart(2, '0');
          const day = String(endDate.getDate()).padStart(2, '0');
          const endDateForCalendar = `${year}-${month}-${day}`;

          const event = {
            id: onCall.id,
            title: `${technician.firstName} ${technician.lastName}`,
            start: startDateForCalendar,
            end: endDateForCalendar,
            backgroundColor: backgroundStyle,
            borderColor: '#e0e0e0',
            textColor: '#000',
            extendedProps: {
              skills: onCall.skills,
              technicianId: onCall.technicianId,
              skillColors: skillColors,
              onCall: onCall
            }
          };

          console.log('Event créé:', event);
          return event;
        }).filter(Boolean);

        console.log('Events formatés:', formattedEvents);
        setEvents(formattedEvents);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
      }
    };

    loadData();
  }, [refreshKey, techniciansLoading, skillsLoading, onCallsLoading, skillsData, onCalls, technicians]);

  useEffect(() => {
    // Forcer le redimensionnement du calendrier quand la sidebar change
    const timer = setTimeout(() => {
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        calendarApi.updateSize();
      }
    }, 300); // Attendre la fin de la transition de la sidebar

    return () => clearTimeout(timer);
  }, [sidebarOpen]);

  const handleEditOnCall = (onCall) => {
    setSelectedOnCall(onCall);
    if (onEditOnCall) {
      onEditOnCall(onCall);
    }
  };

  const handleDeleteClick = () => {
    handleCloseMenu();
    setDeleteConfirmOpen(true);
  };

  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  const handleCloseNotification = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification(prev => ({ ...prev, open: false }));
  };

  const handleEmailClick = () => {
    handleCloseMenu();
    if (selectedOnCall) {
      const loadEmailData = async () => {
        try {
          // Récupérer le technicien
          const technician = technicians.find(t => t.id === selectedOnCall.technicianId);
          if (!technician || !technician.email) {
            throw new Error('Email du technicien non disponible');
          }

          // Récupérer les paramètres de l'équipe depuis la base de données
          const teamSettings = await teamsService.getSettings(selectedTeam?.id);
          const emailSettings = teamSettings?.emailSettings || {};
          
          if (!emailSettings?.htmlTemplate) {
            throw new Error('Le template email n\'est pas configuré pour cette équipe. Veuillez le configurer dans les paramètres.');
          }

          // Préparer les variables pour le modèle
          const startDate = format(new Date(selectedOnCall.startDate), 'dd MMMM yyyy', { locale: fr });
          const endDate = format(new Date(selectedOnCall.endDate), 'dd MMMM yyyy', { locale: fr });
          const skillsList = selectedOnCall.skills
            .map(skillId => skills[skillId]?.name)
            .filter(Boolean)
            .join(', ');

          // Utiliser le template de l'équipe depuis la base de données
          const htmlTemplate = emailSettings.htmlTemplate;

          // Demander au serveur un rendu complet (sections et variables) pour l'aperçu
          let renderedHtml = htmlTemplate;
          try {
            const selectedTeamId = localStorage.getItem('selectedTeamId');
            // Utiliser la même logique de détection d'URL que le service API centralisé
            const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
              ? 'http://localhost:3002'
              : (window.location.hostname.match(/^192\.168\.|^10\.|^172\.(1[6-9]|2[0-9]|3[0-1])\./) 
                ? `${window.location.protocol}//${window.location.hostname}:3002`
                : 'https://api.astreintes.provserver.ovh');

            const previewResp = await fetch(`${API_BASE_URL}/api/render-email-template`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...(selectedTeamId ? { 'x-team-id': selectedTeamId } : {}) },
              body: JSON.stringify({
                html: htmlTemplate,
                to: technician.email,
                technicianId: technician.id,
                calendarEvent: {
                  start: selectedOnCall.startDate,
                  end: selectedOnCall.endDate,
                  skills: selectedOnCall.skills.map(skillId => skills[skillId]?.name).filter(Boolean)
                }
              })
            });
            const previewData = await previewResp.json();
            if (previewResp.ok && previewData?.html) {
              renderedHtml = previewData.html;
            }
          } catch {}

          // Convertir HTML en texte pour l'aperçu plain text
          const textContent = renderedHtml.replace(/<[^>]*>/g, '');

          // Ne pas construire le 'from' côté frontend - laisser le serveur le faire
          // avec les paramètres SMTP globaux et le senderName de l'équipe

          setEmailData({
            to: technician.email,
            from: '', // Le serveur construira automatiquement avec les bons paramètres
            subject: (emailSettings.subject || 'Info astreinte à partir de ce soir 18H')
              .replace(/{startDate}/g, startDate)
              .replace(/{endDate}/g, endDate),
            message: textContent,
            html: renderedHtml
          });
          setEmailConfirmOpen(true);
        } catch (error) {
          showNotification(error.message, 'error');
        }
      };

      loadEmailData();
    }
  };

  const handleSendEmail = async () => {
    try {
      const startDate = new Date(selectedOnCall.startDate);
      const endDate = new Date(selectedOnCall.endDate);

      const calendarEvent = {
        start: startDate,
        end: endDate,
        summary: 'Période d\'astreinte',
        description: emailData.message,
        skills: selectedOnCall.skills
          .map(skillId => skills[skillId]?.name)
          .filter(Boolean)
          .join(', ')
      };

      console.log('Envoi du mail à:', emailData.to);

      // Utiliser la même logique de détection d'URL que le service API centralisé
      const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
        ? 'http://localhost:3002'
        : (window.location.hostname.match(/^192\.168\.|^10\.|^172\.(1[6-9]|2[0-9]|3[0-1])\./) 
          ? `${window.location.protocol}//${window.location.hostname}:3002`
          : 'https://api.astreintes.provserver.ovh');

      const response = await fetch(`${API_BASE_URL}/api/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: emailData.to,
          from: emailData.from,
          subject: emailData.subject,
          html: emailData.html,
          calendarEvent,
          teamId: selectedTeam?.id,
          technicianId: selectedOnCall.technicianId
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors de l\'envoi du mail');
      }

      console.log('Réponse du serveur:', data);
      showNotification('Email envoyé avec succès');
      setEmailConfirmOpen(false);
    } catch (error) {
      console.error('Erreur complète:', error);
      showNotification(error.message, 'error');
    }
  };

  const handleCloseMenu = () => {
    setContextMenu(null);
  };

  const handleDeleteConfirm = async () => {
    if (selectedOnCall) {
      try {
        await deleteOnCall(selectedOnCall.id);
        showNotification('Astreinte supprimée avec succès');
        setDeleteConfirmOpen(false);
        setSelectedOnCall(null);
        if (onDataChange) {
          onDataChange();
        }
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        showNotification('Erreur lors de la suppression', 'error');
      }
    }
  };

  const handleEventClick = useCallback((info) => {
    if (!info.event.extendedProps.isSkill) {
      info.jsEvent.preventDefault();
      
      // Si l'utilisateur ne peut ni éditer ni supprimer, ne pas afficher le menu contextuel
      if (!canEdit('oncalls') && !canDelete('oncalls')) {
        return;
      }
      
      const onCallId = parseInt(info.event.id);
      
      console.log('Clic sur l\'événement ID:', onCallId);
      
      // Chercher l'astreinte dans les données chargées
      const onCall = getOnCallById(onCallId);
      if (onCall) {
        console.log('Astreinte trouvée:', onCall);
        setSelectedOnCall(onCall);
        setContextMenu({
          mouseX: info.jsEvent.clientX,
          mouseY: info.jsEvent.clientY
        });
      } else {
        console.warn('Astreinte non trouvée pour ID:', onCallId);
        showNotification('Astreinte non trouvée', 'error');
      }
    }
  }, [getOnCallById, canEdit, canDelete]);

  // Sauvegarder la date du calendrier quand elle change
  const handleDatesSet = useCallback((dateInfo) => {
    // Utiliser activeStart qui pointe vers la date réelle du mois affiché
    // Ou ajouter 15 jours à currentStart pour être sûr d'être dans le mois principal
    const viewStart = new Date(dateInfo.view.currentStart);
    viewStart.setDate(viewStart.getDate() + 15); // S'assurer d'être au milieu du mois principal
    const currentDate = viewStart.toISOString().split('T')[0];
    localStorage.setItem('calendarDate', currentDate);
  }, []);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, p: 1 }}>
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, multiMonthPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            initialDate={initialDate}
            locale={frLocale}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'multiMonthYear,multiMonthSixMonth,dayGridMonth'
            }}
            views={{
              multiMonthYear: {
                type: 'multiMonth',
                duration: { months: 12 },
                buttonText: 'Année'
              },
              multiMonthSixMonth: {
                type: 'multiMonth',
                duration: { months: 6 },
                buttonText: 'Semestre'
              }
            }}
            weekNumbers={true}
            weekNumberFormat={{ week: 'numeric' }}
            weekText="Semaine "
            events={events}
            eventClick={handleEventClick}
            datesSet={handleDatesSet}
            height="auto"
            eventContent={(eventInfo) => renderEventContent(eventInfo, skills)}
            dayMaxEvents={4}
            eventDisplay="block"
            eventOverlap={false}
            slotEventOverlap={false}
          />
        </Box>
      </Paper>

      <Menu
        open={contextMenu !== null}
        onClose={handleCloseMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {canEdit('oncalls') && (
          <MenuItem onClick={() => {
            handleCloseMenu();
            handleEditOnCall(selectedOnCall);
          }}>
            Modifier
          </MenuItem>
        )}
        {canEdit('oncalls') && (
          <MenuItem onClick={handleEmailClick}>
            Envoyer le mail d'astreinte
          </MenuItem>
        )}
        {canDelete('oncalls') && (
          <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
            Supprimer
          </MenuItem>
        )}
      </Menu>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer cette période d'astreinte ?
            {selectedOnCall && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {format(new Date(selectedOnCall.startDate), 'dd/MM/yyyy')} - {format(new Date(selectedOnCall.endDate), 'dd/MM/yyyy')}
                </Typography>
              </Box>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Annuler</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={emailConfirmOpen}
        onClose={() => setEmailConfirmOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '70vh', maxHeight: '90vh' }
        }}
      >
        <DialogTitle>Envoyer le mail d'astreinte</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Destinataire"
              type="email"
              fullWidth
              value={emailData.to}
              disabled
            />
            <TextField
              label="Objet"
              fullWidth
              value={emailData.subject}
              disabled
            />

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Aperçu HTML</Typography>
              <Box
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: '#fff',
                  p: 2,
                  maxHeight: '50vh',
                  overflow: 'auto',
                  minHeight: '300px',
                  // Styles ReactQuill pour l'aperçu
                  '& .ql-align-center': {
                    textAlign: 'center'
                  },
                  '& .ql-align-right': {
                    textAlign: 'right'
                  },
                  '& .ql-align-left': {
                    textAlign: 'left'
                  },
                  '& .ql-align-justify': {
                    textAlign: 'justify'
                  },
                  '& p': {
                    margin: '8px 0'
                  },
                  '& strong': {
                    fontWeight: 'bold'
                  },
                  '& em': {
                    fontStyle: 'italic'
                  },
                  '& u': {
                    textDecoration: 'underline'
                  },
                  '& a': {
                    color: '#0066cc',
                    textDecoration: 'underline'
                  }
                }}
                dangerouslySetInnerHTML={{ __html: emailData.html || '' }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailConfirmOpen(false)}>Annuler</Button>
          <Button 
            onClick={handleSendEmail} 
            variant="contained" 
            color="primary"
          >
            Envoyer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const renderEventContent = (eventInfo, skillsMap) => {
  const { event } = eventInfo;
  const { extendedProps } = event;

  // Récupérer les noms des compétences avec leurs couleurs
  const skillsInfo = extendedProps.skills
    .map(skillId => {
      const skill = skillsMap[skillId];
      return skill ? {
        name: skill.name,
        color: skill.color
      } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Box sx={{
      p: '2px 4px',
      fontSize: '0.85em',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      height: '20px',
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      borderRadius: '2px',
      overflow: 'hidden'
    }}>
      <Typography sx={{ 
        fontWeight: 'bold',
        fontSize: 'inherit',
        whiteSpace: 'nowrap',
        minWidth: 'fit-content',
        flexShrink: 0
      }}>
        {event.title}
      </Typography>
      {skillsInfo.length > 0 && (
        <>
          <Typography sx={{ 
            color: 'text.secondary', 
            px: 0.5,
            flexShrink: 0
          }}>
            -
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            gap: 0.5, 
            overflow: 'hidden',
            flex: 1,
            height: '100%',
            alignItems: 'center'
          }}>
            {skillsInfo.map((skill, index) => (
              <Typography
                key={index}
                sx={{
                  fontSize: '0.8em',
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                  backgroundColor: `${skill.color}20`,
                  px: 0.5,
                  py: '1px',
                  borderRadius: '2px',
                  border: `1px solid ${skill.color}40`,
                  flexShrink: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100px',
                  height: 'fit-content',
                  lineHeight: 1.2
                }}
              >
                {skill.name}
              </Typography>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default PlanningCalendar;